import { byId, groupedBy, pileOf } from '../lib/grouped';
import { contributionSpeaker, portraitPerson, storyTeller } from './piles';
import type { FamilyPhoto, Person, PersonPortrait, Story, StoryContribution, UUID } from './types';

/**
 * Memory badges (section 9.7, extended). Anything new about a person shows up
 * as a small badge sitting on the rim of their circle: a story badge (story
 * icon) for each published story or contribution, and a photo badge (the photo
 * itself) for each picture added to their circle. Badges start large and shrink
 * over 14 days to a permanent small mark; past eight, the newest eight are shown
 * and the last one carries the remaining count.
 */
export const FRESH_DAYS = 14;
export const MAX_INDIVIDUAL_DOTS = 8;
export const DOT_MAX_PX = 30;
export const DOT_MIN_PX = 16;

/** story = a story or contribution; photo = a portrait added to the circle; moment = a photo or event this person shared in Moments. */
export type MemoryDotKind = 'story' | 'photo' | 'moment';

export interface MemoryDot {
  kind: MemoryDotKind;
  /** Story id for a story badge; portrait id for a photo badge. */
  id: UUID;
  storyId: UUID;
  ageDays: number;
  /** 0 = brand new, 1 = fully settled */
  settled: number;
  sizePx: number;
  fresh: boolean;
}

export interface BadgeTuning {
  maxPx: number;
  minPx: number;
  fadeDays: number;
}

export const DEFAULT_BADGE_TUNING: BadgeTuning = { maxPx: DOT_MAX_PX, minPx: DOT_MIN_PX, fadeDays: FRESH_DAYS };

export interface MemoryIndicator {
  dots: MemoryDot[];
  /** Stories and contributions only — what "n stories" means everywhere else. */
  total: number;
  /** All badges, stories and photos. */
  totalAll: number;
  grouped: boolean;
  freshest: number; // 0..1 settled value of the freshest dot (0 = new)
  hasFresh: boolean;
  /**
   * 0..1 — how "new" this person is right now, taking the newest of: the
   * circle being created, a story, a photo. Drives the circle's size.
   */
  freshness: number;
}

export const EMPTY_MEMORY: MemoryIndicator = { dots: [], total: 0, totalAll: 0, grouped: false, freshest: 1, hasFresh: false, freshness: 0 };

/*
 * The tree asks this of every person in the family, one after another, so the
 * lists are sorted into piles by person once (lib/grouped, domain/piles) and
 * each person is handed their own few — rather than every person walking every
 * story, every recording and every photograph the family has ever added.
 */
export function memoryDotsFor(
  personId: UUID,
  stories: Story[],
  contributions: StoryContribution[],
  portraits: PersonPortrait[] = [],
  now: number = Date.now(),
  tuning: BadgeTuning = DEFAULT_BADGE_TUNING,
  person?: Pick<Person, 'createdAt'>,
  nodeFadeDays: number = FRESH_DAYS,
  moments: FamilyPhoto[] = [],
): MemoryIndicator {
  const dots: MemoryDot[] = [];
  const seen = new Set<string>();
  for (const s of pileOf(groupedBy(stories, storyTeller), personId)) {
    if (s.status !== 'published' || !s.publishedAt) continue;
    dots.push(makeDot('story', s.id, s.id, s.publishedAt, now, tuning));
    seen.add(s.id);
  }
  const storyOf = byId(stories);
  for (const c of pileOf(groupedBy(contributions, contributionSpeaker), personId)) {
    if (c.status !== 'published') continue;
    const key = `${c.storyId}:${c.id}`;
    if (seen.has(key)) continue;
    const story = storyOf.get(c.storyId);
    if (!story || story.status !== 'published') continue;
    // The primary storyteller's own first contribution is already represented by the story dot.
    if (story.primaryStorytellerPersonId === personId && c.order === 0) continue;
    dots.push(makeDot('story', c.id, c.storyId, c.createdAt, now, tuning));
    seen.add(key);
  }
  const storyCount = dots.length;
  for (const p of pileOf(groupedBy(portraits, portraitPerson), personId)) dots.push(makeDot('photo', p.id, p.id, p.createdAt, now, tuning));
  for (const m of moments) dots.push(makeDot('moment', m.id, m.id, m.createdAt, now, tuning));
  dots.sort((a, b) => a.ageDays - b.ageDays);
  const freshest = dots.length ? dots[0].settled : 1;
  // Newest happening for the circle itself: creation, story or photo — whichever is most recent.
  let newestAge = dots.length ? dots[0].ageDays : Infinity;
  if (person?.createdAt) newestAge = Math.min(newestAge, Math.max(0, (now - new Date(person.createdAt).getTime()) / 86_400_000));
  const freshness = Number.isFinite(newestAge) ? 1 - easeOut(Math.min(1, newestAge / Math.max(0.01, nodeFadeDays))) : 0;
  return {
    dots: dots.slice(0, MAX_INDIVIDUAL_DOTS),
    total: storyCount,
    totalAll: dots.length,
    grouped: dots.length >= MAX_INDIVIDUAL_DOTS,
    freshest,
    hasFresh: dots.some((d) => d.fresh),
    freshness,
  };
}

/**
 * The badges for the whole family at once — what the tree and the globe both
 * draw their circles from.
 *
 * A Moments post belongs to the circle of whoever shared it (account → person);
 * being pinned in somebody else's photograph does not put it on yours. Both
 * screens used to work this out for themselves, and both did it by copying each
 * person's list again for every picture they had ever shared — which is fine
 * for a dozen and quadratic for the aunt who uploads everything.
 */
export function memoryForEveryone(
  people: ReadonlyArray<Pick<Person, 'id' | 'createdAt'>>,
  stories: Story[],
  contributions: StoryContribution[],
  portraits: PersonPortrait[],
  moments: FamilyPhoto[],
  links: ReadonlyArray<{ accountId: UUID; personId: UUID; status: string }>,
  settings: { badgeMaxPx: number; badgeMinPx: number; badgeFadeDays: number; nodeFadeDays: number },
  now: number = Date.now(),
): Map<UUID, MemoryIndicator> {
  const tuning: BadgeTuning = { maxPx: settings.badgeMaxPx, minPx: settings.badgeMinPx, fadeDays: settings.badgeFadeDays };
  const personOfAccount = new Map<UUID, UUID>();
  for (const l of links) if (l.status === 'active') personOfAccount.set(l.accountId, l.personId);
  const sharedBy = new Map<UUID, FamilyPhoto[]>();
  for (const m of moments) {
    const pid = personOfAccount.get(m.uploadedByAccountId);
    if (!pid) continue;
    const pile = sharedBy.get(pid);
    if (pile) pile.push(m);
    else sharedBy.set(pid, [m]);
  }
  const out = new Map<UUID, MemoryIndicator>();
  for (const p of people) out.set(p.id, memoryDotsFor(p.id, stories, contributions, portraits, now, tuning, p, settings.nodeFadeDays, sharedBy.get(p.id) ?? NO_MOMENTS));
  return out;
}

const NO_MOMENTS: FamilyPhoto[] = [];

/** ease-out so the shrink is noticeable early and gentle later */
export function easeOut(x: number): number {
  return 1 - Math.pow(1 - x, 2);
}

/**
 * How much bigger something is drawn for being new.
 *
 * The badges on a circle in the tree start large and shrink to a permanent
 * small mark over `fadeDays` — so "is this new?" is answered by size alone,
 * without a word or a colour. A constellation's nodes now answer it the same
 * way, on the same clock and with the same easing, so a thing that is big on
 * somebody's circle in the tree is big again when you open them.
 *
 * Only the *depth* differs: a badge is 16px settling from 30, which is nearly
 * double, and doubling a 76px node would make it bigger than the face in the
 * middle. `FRESH_PEAK` is the constellation's ceiling; the timing is shared
 * (`treeSettings.badgeFadeDays`), which is what an administrator actually
 * tunes.
 */
export const FRESH_PEAK = 1.45;

export function freshScale(iso: string | undefined, now: number, fadeDays: number, peak = FRESH_PEAK): number {
  if (!iso) return 1;
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return 1;
  const ageDays = Math.max(0, (now - at) / 86_400_000);
  const settled = Math.min(1, ageDays / Math.max(0.01, fadeDays));
  return 1 + (peak - 1) * (1 - easeOut(settled));
}

/** Days since something arrived — how `MemoryDot.fresh` is decided, for anything else that wants it. */
export function ageInDays(iso: string | undefined, now: number = Date.now()): number {
  if (!iso) return Infinity;
  const at = Date.parse(iso);
  return Number.isNaN(at) ? Infinity : Math.max(0, (now - at) / 86_400_000);
}

function makeDot(kind: MemoryDotKind, id: UUID, storyId: UUID, iso: string, now: number, tuning: BadgeTuning): MemoryDot {
  const ageDays = Math.max(0, (now - new Date(iso).getTime()) / 86_400_000);
  const settled = Math.min(1, ageDays / Math.max(0.01, tuning.fadeDays));
  return {
    kind,
    id,
    storyId,
    ageDays,
    settled,
    sizePx: Math.round((tuning.maxPx - (tuning.maxPx - tuning.minPx) * easeOut(settled)) * 10) / 10,
    fresh: ageDays < 3,
  };
}
